import { useBuild } from "app/components/build/Show/lib/BuildContext";
import BuildMessage from "app/components/build/Message";
import { Commit } from "app/components/build/Header/Commit";
import { PullRequest } from "app/components/build/Header/PullRequest";
import UserAvatar from "app/components/shared/UserAvatar";
import Icon from "app/components/shared/Icon";
import Emojify from "app/components/shared/Emojify";
import { capitalize } from "lodash";
import { useNavigate } from "react-router-dom";
import { Build } from "../../lib/types";
import FriendlyTime from "app/components/shared/FriendlyTime";

const labelsForSource: { [key: string]: string } = {
  ui: "website",
  webhook: "webhook",
  api: "API",
  trigger_job: "pipeline",
  schedule: "schedule",
};

const BuildSource = ({ build }: { build: Build }) => {
  const label = labelsForSource[build.source] || "unknown";

  if (build.rebuiltFrom) {
    return (
      <div className="text-xs sm:text-sm">
        Rebuild of{" "}
        <a
          href={build.rebuiltFrom.url}
          className="font-medium text-inherit hover:text-inherit focus:text-inherit"
        >
          #{build.rebuiltFrom.number}
        </a>{" "}
        from {label}
      </div>
    );
  }

  if (build.triggeredFrom) {
    if (build.triggeredFrom.project && build.triggeredFrom.build) {
      return (
        <span>
          Triggered from{" "}
          <a
            href={build.triggeredFrom.url}
            className="font-medium text-inherit hover:text-inherit focus:text-inherit"
          >
            <Emojify text={build.triggeredFrom.project.name} /> (#{build.triggeredFrom.build.number}
            )
          </a>
        </span>
      );
    }
    // Most likely the pipeline is one this user doesn't have access to.
    return <span>Triggered from pipeline</span>;
  }

  return <span>{capitalize(label)}</span>;
};

// Map annotation styles to colors for badges
const annotationBadgeColors: { [key: string]: string } = {
  error: "bg-red-100 text-red-700 border border-red-200",
  warning: "bg-orange-100 text-orange-700 border border-orange-200",
  info: "bg-blue-100 text-blue-700 border border-blue-200",
  success: "bg-lime-100 text-lime-700 border border-lime-200",
  default: "bg-gray-100 text-gray-700 border border-gray-200",
};

export default function BuildContextCard() {
  const { build } = useBuild();
  const navigate = useNavigate();

  // Calculate annotation counts
  const annotationCounts = build.annotationCountsByStyle || {};
  const presentStyles = Object.keys(annotationCounts).filter((key) => annotationCounts[key] > 0);
  const totalAnnotationCount = presentStyles.reduce((sum, key) => sum + annotationCounts[key], 0);

  if (!build.source && !build.commitId && !build.authorName) {
    return null;
  }

  return (
    <div
      className="flex h-full flex-auto flex-col rounded-md bg-white p-2 shadow-depth-200 sm:p-4"
      data-testid="trigger-source-card"
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="my-0 inline max-w-[50%] truncate text-base font-semibold sm:text-lg">
          Build #{build.number}
        </h2>
      </div>

      <div className="flex flex-auto flex-col gap-y-3">
        <div className="col-span-2">
          <BuildMessage
            message={build.message}
            className="line-clamp-3 text-base font-medium text-charcoal-700"
          />
        </div>

        <div className="text-xs sm:text-sm">
          <div className="flex flex-wrap gap-4">
            {build.commitId && (
              <div className="min-w-[calc(50%-8px)] flex-1">
                <span className="mb-0.5 block text-gray-900">Commit</span>
                <span className="inline-flex items-center gap-1 text-charcoal-700">
                  <Commit {...build} className="peer inline-flex text-xs font-medium sm:text-sm" />
                  <Icon
                    icon="heroicons/outline/arrow-up-right"
                    className="invisible h-3 w-3 peer-hover:visible"
                  />
                </span>
              </div>
            )}

            {build.branchName && (
              <div className="min-w-[calc(50%-8px)] flex-1">
                <span className="mb-0.5 block text-gray-900">Branch</span>
                <a
                  href={build.branchPath}
                  className="inline-flex max-w-full items-center gap-1 font-mono text-[90%] font-medium text-charcoal-700 hover:text-charcoal-900"
                >
                  <Icon
                    icon="custom/outline/git-branch"
                    className="h-4 w-4 shrink-0 text-gray-900"
                  />
                  <Emojify text={build.branchName} className="truncate" />
                </a>
              </div>
            )}
          </div>
        </div>

        {build.pullRequest && (
          <div className="col-span-2">
            <span className="mb-0.5 block text-gray-900">
              {build.project.provider.id === "gitlab" ? "Merge request" : "Pull request"}
            </span>
            <PullRequest {...build} className="text-xs font-medium sm:text-sm" />
          </div>
        )}

        {/* Divider */}
        <div className="col-span-2 border-b" />

        <div className="text-xs sm:text-sm">
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[calc(50%-8px)] flex-1">
              <span className="mb-0.5 block text-gray-900">Created</span>

              <div className="text-charcoal-700">
                <FriendlyTime
                  value={build.createdAt}
                  capitalized={true}
                  toggleable={false}
                  clickable={false}
                />
              </div>
            </div>

            {build.authorName && (
              <div className="min-w-[calc(50%-8px)] flex-1">
                <span className="mb-0.5 block text-gray-900">Author</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  <UserAvatar
                    size="SMALL"
                    user={{
                      name: build.authorName || "Unknown",
                      avatar: { url: build.authorAvatar || "" },
                    }}
                  />
                  <span className="text-xs sm:text-sm">{build.authorName || "System"}</span>
                  {build.creatorVerified && (
                    <span className="build-verified ml-0">
                      <Icon icon="check" className="h-3 w-3" />
                      Verified
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="min-w-[calc(50%-8px)] flex-1">
              <span className="mb-0.5 block text-gray-900">Source</span>
              <div className="flex items-center gap-1 text-charcoal-700">
                <BuildSource build={build} />
              </div>
            </div>
          </div>
        </div>
        {totalAnnotationCount > 0 && (
          <div className="mt-auto border-t pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {presentStyles.map((style) => (
                  <span
                    key={style}
                    className={`rounded px-2 py-1 text-xs font-medium ${annotationBadgeColors[style] || annotationBadgeColors.default}`}
                  >
                    {annotationCounts[style]}
                  </span>
                ))}
                <span className="text-xs font-medium sm:text-sm">
                  {totalAnnotationCount} annotation{totalAnnotationCount !== 1 ? "s" : ""}
                </span>
              </div>
              <button
                onClick={() => navigate("../annotations", { relative: "path" })}
                className="transition-color flex items-center gap-1 rounded border border-slate-400 bg-white px-1.5 py-0.5 text-xs font-medium text-charcoal-700 no-underline shadow-sm hover:bg-gray-100 hover:text-charcoal-600 focus:text-charcoal-600 active:text-charcoal-600 sm:text-sm"
              >
                View all <Icon icon="arrow-right" className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
